import React, { useCallback, useEffect, useMemo } from 'react';
import { Modal, SubmitButton } from 'framework/ui/ui-builder';
import { CreateForm, DEFAULT_ERROR_MESSAGE, useFormContext } from 'framework/form';
import { EducationListInput, useEducationService } from 'domain/shared/employee';
import { Education } from '../shared/types';
import { useMessageService, ErrorMessage } from 'framework/ui/Message';
import { useArtifactService } from 'domain/shared/artifact';
import { usePrepopulationContext } from 'domain/people/employee/prepopulation/PrepopulationContext';

interface EducationPrepopulateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data?: { extendedInformation?: { educations?: Education[] } }) => void;
}

const ModalContent = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
  const { submitForm, isSubmitEnabled } = useFormContext();

  return (
    <Modal
      isOpen={isOpen}
      onToggle={onClose}
      hasCloseIcon
      title="Education"
      modalSize="md"
      paperStyles={{
        background: 'var(--mui-palette-background-paper)',
      }}
      buttons={[<SubmitButton key="save" onClick={submitForm} isLoading={!isSubmitEnabled} />]}
    >
      <EducationListInput source="request" isAddable={false} />
    </Modal>
  );
};

const EducationPrepopulateModal: React.FC<EducationPrepopulateModalProps> = ({ isOpen, onClose, onSave }) => {
  const { create, getCreateValidationSchema } = useEducationService();
  const { addMessage } = useMessageService();
  const { setArtifactsToStore } = useArtifactService();
  const { data, loading } = usePrepopulationContext();

  useEffect(() => {
    if (data?.artifacts) {
      setArtifactsToStore(data.artifacts);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.artifacts]);

  const prepopulatedData = useMemo(() => {
    if (loading || !data || Object.keys(data).length === 0) {
      return null;
    }

    const educations = data?.info?.data?.educations || data?.educations;
    const result = educations?.map((education: any) => ({
      ...education,
      haveDocument: !!(education.document && education.document.type),
    }));

    return { request: result || [] };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const handleSubmit = useCallback(async (formData: any) => {
    try {
      await create(formData);
      onSave();
      onClose();
      return formData;
    } catch (error) {
      addMessage(new ErrorMessage(DEFAULT_ERROR_MESSAGE));
      return Promise.reject(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading || !prepopulatedData) {
    return null;
  }

  return (
    <CreateForm
      submitFormFunc={handleSubmit}
      initialData={prepopulatedData}
      getValidationSchemaFunc={getCreateValidationSchema}
    >
      <ModalContent isOpen={isOpen} onClose={onClose} />
    </CreateForm>
  );
};

export default EducationPrepopulateModal;
